import React from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, BedDouble, Bath, Maximize2, DollarSign, MapPin } from 'lucide-react';

// ─── Design tokens (mirrors BuyerDashboard) ───────────────────────────────────
const C = {
  bg:      '#0E1520',
  surface: '#16202E',
  coral:   '#E07A5F',
  gold:    '#C4A87A',
  text:    '#F8F9FA',
  muted:   '#C8D3DF',
  border:  'rgba(255,255,255,0.08)',
  green:   'rgba(52,211,153,0.88)',
  amber:   'rgba(251,191,36,0.82)',
};
const editorial = { fontFamily: "'Cormorant Garamond', 'Cormorant', Georgia, serif" };
const sans      = { fontFamily: "'Outfit', system-ui, sans-serif" };
const serif     = { fontFamily: "'Playfair Display', Georgia, serif" };

function formatPrice(p) {
  if (!p && p !== 0) return '–';
  if (p >= 1_000_000) {
    const m = p / 1_000_000;
    return '$' + (m % 1 === 0 ? m.toFixed(0) : m.toFixed(2).replace(/0+$/, '')) + 'M';
  }
  if (p >= 1_000) return '$' + Math.round(p / 1_000) + 'K';
  return '$' + p;
}

function ScoreRing({ score }) {
  const size   = 68;
  const stroke = 5;
  const r      = (size - stroke) / 2;
  const circ   = 2 * Math.PI * r;
  const offset = circ - (score / 100) * circ;
  const color  = score >= 80 ? C.coral : score >= 65 ? C.gold : 'rgba(200,211,223,0.45)';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', position: 'absolute' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke="rgba(255,255,255,0.07)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" />
      </svg>
      <span className="font-bold text-base" style={{ ...sans, color, zIndex: 1 }}>{score}</span>
    </div>
  );
}

function HomeColumn({ listing, label, delay }) {
  const photo     = listing.photos?.[0] || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80';
  const reasons   = listing.match_reasons   || [];
  const conflicts = listing.match_conflicts || [];
  const score     = listing.match_score || 0;
  const navigate  = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
      className="flex flex-col"
      style={{ background: C.surface, border: '1px solid ' + C.border, borderRadius: '1.25rem', overflow: 'hidden' }}
      data-testid={'compare-column-' + label.toLowerCase()}>

      {/* Photo */}
      <div className="relative" style={{ height: '240px', flexShrink: 0 }}>
        <img src={photo} alt={listing.address}
          className="w-full h-full object-cover"
          onError={e => { e.target.src = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80'; }} />
        <div className="absolute inset-0"
          style={{ background: 'linear-gradient(to top, rgba(14,21,32,0.95) 0%, rgba(14,21,32,0.2) 60%, transparent 100%)' }} />

        {/* Label badge */}
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-semibold"
          style={{ ...sans, background: 'rgba(14,21,32,0.82)', backdropFilter: 'blur(10px)', color: 'rgba(200,211,223,0.65)', border: '1px solid rgba(255,255,255,0.12)', letterSpacing: '0.06em' }}>
          {label}
        </div>

        {/* Match score ring */}
        <div className="absolute top-3 right-4">
          <ScoreRing score={score} />
        </div>

        {/* Address */}
        <div className="absolute bottom-4 left-4 right-4">
          <p className="font-semibold text-base leading-snug" style={{ ...sans, color: C.text }}>{listing.address}</p>
          <p className="flex items-center gap-1 text-xs mt-0.5" style={{ ...sans, color: 'rgba(200,211,223,0.6)' }}>
            <MapPin size={10} />{listing.city}, {listing.state}
          </p>
        </div>
      </div>

      {/* Stats */}
      <div className="px-5 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.07)' }}>
        <p className="text-2xl font-bold mb-3" style={{ ...serif, color: C.text }}>{formatPrice(listing.price)}</p>
        <div className="flex items-center gap-4">
          {[
            { icon: <BedDouble size={13} />, val: listing.beds,                        label: 'beds' },
            { icon: <Bath size={13} />,      val: listing.baths,                       label: 'baths' },
            { icon: <Maximize2 size={12} />, val: listing.sqft?.toLocaleString() || '–', label: 'sqft' },
          ].map(({ icon, val, label: l }, i) => (
            <div key={i} className="flex items-center gap-1.5 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.6)' }}>
              <span style={{ color: 'rgba(200,211,223,0.4)' }}>{icon}</span>
              <span><span style={{ color: C.muted, fontWeight: 600 }}>{val}</span> {l}</span>
            </div>
          ))}
        </div>
        {listing.has_hoa && listing.hoa_amount > 0 && (
          <div className="flex items-center gap-1.5 mt-2 text-xs" style={{ ...sans, color: 'rgba(200,211,223,0.45)' }}>
            <DollarSign size={11} />HOA {formatPrice(listing.hoa_amount)}/mo
          </div>
        )}
      </div>

      {/* What works */}
      <div className="px-5 py-4 flex-1">
        {reasons.length > 0 ? (
          <div className="mb-4">
            <p className="mb-2.5" style={{ ...sans, color: 'rgba(52,211,153,0.45)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              What works
            </p>
            <div className="space-y-1.5">
              {reasons.map((r, i) => (
                <div key={i} className="flex items-start gap-2" style={{ ...sans, color: C.green, fontSize: '0.8rem', lineHeight: 1.55 }}>
                  <span style={{ color: '#34D399', fontSize: '0.55rem', marginTop: '0.35rem', flexShrink: 0 }}>&#10004;</span>
                  {r}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <p className="text-xs italic mb-4" style={{ ...sans, color: 'rgba(200,211,223,0.3)' }}>No match data yet.</p>
        )}

        {conflicts.length > 0 && (
          <div className="mb-4">
            <p className="mb-2.5" style={{ ...sans, color: 'rgba(251,191,36,0.4)', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
              Worth considering
            </p>
            <div className="space-y-1.5">
              {conflicts.map((cf, i) => (
                <div key={i} className="flex items-start gap-2" style={{ ...sans, color: C.amber, fontSize: '0.8rem', lineHeight: 1.55 }}>
                  <span style={{ color: '#FBBF24', fontSize: '0.55rem', marginTop: '0.35rem', flexShrink: 0 }}>&#9702;</span>
                  {cf}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* CTA */}
      <div className="px-5 pb-5">
        <button
          onClick={() => navigate(`/listing/${listing.id}`, { state: { listing } })}
          data-testid={'compare-view-' + listing.id}
          className="w-full py-3 rounded-full text-sm font-semibold transition-all"
          style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 20px rgba(224,122,95,0.25)' }}
          onMouseEnter={e => { e.currentTarget.style.opacity = '0.88'; }}
          onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}>
          View full details
        </button>
      </div>
    </motion.div>
  );
}

export default function ComparePage() {
  const location = useLocation();
  const navigate  = useNavigate();
  const { homeA, homeB } = location.state || {};

  // If accessed directly without state, redirect to saved
  if (!homeA || !homeB) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6"
        style={{ background: C.bg }}>
        <p className="mb-6 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)' }}>
          Select two saved homes to compare.
        </p>
        <Link to="/saved"
          className="px-5 py-2.5 rounded-full text-sm font-semibold transition-all"
          style={{ ...sans, background: C.coral, color: '#fff' }}>
          Go to saved matches
        </Link>
      </div>
    );
  }

  const scoreA = homeA.match_score || 0;
  const scoreB = homeB.match_score || 0;
  const winner = scoreA > scoreB ? 'A' : scoreB > scoreA ? 'B' : null;

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 pb-20" style={{ background: C.bg }}>

      {/* Ambient top glow */}
      <div className="fixed top-0 left-0 right-0 h-64 pointer-events-none" style={{
        background: 'radial-gradient(ellipse at 50% -10%, rgba(196,168,122,0.07), transparent 65%)',
        zIndex: 0,
      }} />

      <div className="relative z-10 max-w-4xl mx-auto">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="pt-8 pb-8 flex items-start justify-between gap-4">
          <div>
            <button
              onClick={() => navigate(-1)}
              data-testid="compare-back-btn"
              className="flex items-center gap-2 text-xs mb-5 transition-opacity hover:opacity-70"
              style={{ ...sans, color: 'rgba(200,211,223,0.45)', background: 'none', border: 'none', padding: 0 }}>
              <ArrowLeft size={14} /> Back to saved
            </button>
            <h1 style={{ ...editorial, color: C.text, fontSize: 'clamp(2rem,5vw,3.2rem)', fontWeight: 600, letterSpacing: '-0.025em', lineHeight: 1.05 }}>
              Head to head.
            </h1>
            <p className="mt-2 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)', lineHeight: 1.7 }}>
              Two homes. One decision. Which one feels like yours?
            </p>
          </div>
        </motion.div>

        {/* Comparison columns */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10"
          data-testid="compare-grid">
          <HomeColumn listing={homeA} label="Option A" delay={0.1} />
          <HomeColumn listing={homeB} label="Option B" delay={0.2} />
        </div>

        {/* Verdict */}
        <motion.div
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.55 }}
          className="rounded-2xl px-6 py-8 text-center"
          style={{ background: C.surface, border: '1px solid rgba(196,168,122,0.15)' }}
          data-testid="compare-verdict">

          {winner ? (
            <>
              <p className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ ...sans, color: 'rgba(196,168,122,0.5)', letterSpacing: '0.14em' }}>
                The numbers say
              </p>
              <p style={{ ...editorial, color: C.gold, fontSize: 'clamp(1.6rem,4vw,2.4rem)', fontWeight: 600, fontStyle: 'italic', lineHeight: 1.1 }}>
                Option {winner} is your closer match.
              </p>
              <p className="mt-3 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)', lineHeight: 1.75 }}>
                But numbers only tell part of the story.<br />
                Which one did you picture yourself coming home to?
              </p>
            </>
          ) : (
            <>
              <p className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ ...sans, color: 'rgba(196,168,122,0.5)', letterSpacing: '0.14em' }}>
                Perfectly matched
              </p>
              <p style={{ ...editorial, color: C.gold, fontSize: 'clamp(1.6rem,4vw,2.4rem)', fontWeight: 600, fontStyle: 'italic', lineHeight: 1.1 }}>
                It's a tie.
              </p>
              <p className="mt-3 text-sm" style={{ ...sans, color: 'rgba(200,211,223,0.5)', lineHeight: 1.75 }}>
                Both score equally well for you.<br />
                This one comes down to feeling.
              </p>
            </>
          )}

          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/saved"
              className="px-6 py-3 rounded-full text-sm font-semibold transition-all"
              style={{ ...sans, background: C.coral, color: '#fff', letterSpacing: '0.02em', boxShadow: '0 4px 20px rgba(224,122,95,0.25)' }}
              data-testid="compare-back-saved-btn">
              Back to saved matches
            </Link>
            <Link to="/discover"
              className="px-6 py-3 rounded-full text-sm font-medium transition-all"
              style={{ ...sans, color: 'rgba(200,211,223,0.55)', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent' }}>
              Keep browsing
            </Link>
          </div>
        </motion.div>

      </div>
    </div>
  );
}
